import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENTS = [
  { value: "need", label: "I Need Something" },
  { value: "offer", label: "I Can Offer Something" },
  { value: "both", label: "Both" },
];

export const Founding100 = () => {
  const [count, setCount] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", intent_type: "both", details: "" });
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/founding-members/count`)
      .then((r) => setCount(r.data.count))
      .catch(() => setCount(0));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/founding-members`, form);
      setJoined(true);
      setCount((c) => (c ?? 0) + 1);
      toast.success("Welcome to the Founding 100.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="founding-100" data-testid="founding-section" className="relative py-28 lg:py-40 border-t border-white/5 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 45% 50% at 78% 50%, rgba(138,43,226,0.1), transparent 65%)" }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        <div className="min-w-0">
          <Reveal>
            <span className="font-mono-accent text-xs tracking-[0.3em] text-[#00F0FF]" data-testid="founding-eyebrow">
              /// FOUNDING 100
            </span>
            <h2 className="mt-6 font-display font-extrabold uppercase text-3xl sm:text-4xl lg:text-5xl leading-[1.02] text-slate-100" data-testid="founding-title">
              We're building FINDXCHANGE from the <span className="text-[#00F0FF]">ground up</span>.
            </h2>
            <p className="mt-6 text-slate-400 text-base sm:text-lg leading-relaxed max-w-lg">
              Be one of the first 100 people helping shape the network. Founding members get
              early access and a direct voice in how FINDXCHANGE is built.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-wrap items-end gap-x-5 gap-y-4" data-testid="founding-counter">
              <span className="font-display font-extrabold text-6xl sm:text-7xl lg:text-8xl leading-none text-slate-100 tabular-nums">
                {count === null ? "—" : String(count).padStart(2, "0")}
              </span>
              <div className="pb-2 min-w-0">
                <span className="block font-display font-bold text-xl sm:text-2xl text-slate-500">/ 100</span>
                <span className="block font-mono-accent text-[10px] tracking-[0.18em] sm:tracking-[0.3em] text-[#00F0FF] mt-1">
                  FOUNDING SPOTS CLAIMED
                </span>
              </div>
            </div>
            <div className="mt-6 h-px w-full max-w-md bg-white/10 relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-[#00F0FF] transition-all duration-1000"
                style={{ width: `${Math.min(((count ?? 0) / 100) * 100, 100)}%`, boxShadow: "0 0 12px rgba(0,240,255,0.6)" }}
              />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="min-w-0">
          <div className="card-frame p-8 sm:p-10" data-testid="founding-form-card">
            {joined ? (
              <div className="py-10 text-center" data-testid="founding-success">
                <span className="mx-auto w-14 h-14 grid place-items-center border border-[#00F0FF]/60 text-[#00F0FF]">
                  <Check size={26} />
                </span>
                <h3 className="mt-7 font-display font-extrabold uppercase text-2xl text-slate-100">
                  You're at the X.
                </h3>
                <p className="mt-4 text-slate-400 text-sm sm:text-base leading-relaxed max-w-sm mx-auto">
                  Your founding member application is in. We'll be in touch as the network comes online.
                </p>
                <button
                  data-testid="founding-register-another-btn"
                  onClick={() => {
                    setJoined(false);
                    setForm({ full_name: "", email: "", intent_type: "both", details: "" });
                  }}
                  className="btn-ghost mt-9 !px-6 !py-3"
                >
                  Register Another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="founding-form">
                <div>
                  <label htmlFor="founding-name" className="block font-mono-accent text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2.5">
                    Full Name / Organization
                  </label>
                  <input
                    id="founding-name"
                    data-testid="founding-form-name-input"
                    type="text"
                    required
                    minLength={2}
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    placeholder="e.g. Alex Carter"
                    className="field-input"
                  />
                </div>
                <div>
                  <label htmlFor="founding-email" className="block font-mono-accent text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2.5">
                    Direct Contact Email
                  </label>
                  <input
                    id="founding-email"
                    data-testid="founding-form-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@company.com"
                    className="field-input"
                  />
                </div>
                <div>
                  <span className="block font-mono-accent text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2.5">
                    I'm here because
                  </span>
                  <div className="grid grid-cols-3 gap-2" data-testid="founding-form-intent-group">
                    {INTENTS.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        data-testid={`founding-form-intent-${o.value}`}
                        onClick={() => setForm({ ...form, intent_type: o.value })}
                        className={`px-2 py-3 font-mono-accent text-[10px] sm:text-[11px] uppercase tracking-[0.12em] border transition-all duration-300 ${
                          form.intent_type === o.value
                            ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/5 shadow-[0_0_18px_rgba(0,240,255,0.15)]"
                            : "border-white/10 text-slate-500 hover:border-white/30 hover:text-slate-300"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="founding-details" className="block font-mono-accent text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2.5">
                    What do you need, or what can you offer?
                  </label>
                  <textarea
                    id="founding-details"
                    data-testid="founding-form-details-input"
                    required
                    minLength={5}
                    rows={4}
                    value={form.details}
                    onChange={(e) => setForm({ ...form, details: e.target.value })}
                    placeholder="e.g. I run a logistics firm with spare warehouse capacity in the Midlands…"
                    className="field-input resize-none"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="founding-form-submit-btn"
                  disabled={submitting}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Transmitting…" : "Become a Founding Member"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
